import { optionSchema } from '@/common/schemas/option.schema'
import { ProductData } from '@/common/types/product.type'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { z } from 'zod'

const geosSchema = z.object({
  value: z.string().optional(),
  type: z.string().optional(),
  label: z.custom<JSX.Element | undefined>().optional().nullable(),
})

export const contractProductPriceSchema = z.object({
  id: z.string().optional().nullable(),
  value: z.string().optional().nullable(),
  geo: geosSchema.optional().nullable(),
})

export type UpdateContractProductPriceValidationSchema = z.infer<typeof contractProductPriceSchema>

export const contractProductSchema = z.object({
  id: z.string().optional(),
  product: z.custom<ProductData>(),
  global_value_in_unit_for_consumption: z.string(),
  global_value_in_currency: z.string(),
  price_rule: optionSchema.optional(),
  contract_product_category_product_prices: z.array(contractProductPriceSchema).optional().nullable(),
  filterByRegion: z.boolean().default(false).optional(),
})

export type UpdateContractProductValidationSchema = z.infer<typeof contractProductSchema>

export const contractProductCategorySchema = z.object({
  id: z.string().optional(),
  product_category: z.custom<ProductCategoryData>(),
  global_value_in_unit_for_consumption: z.string().optional(),
  global_value_in_currency: z.string().optional(),
  contract_product_category_products: z.array(contractProductSchema).optional().nullable(),
})

export type UpdateContractProductCategoryValidationSchema = z.infer<typeof contractProductCategorySchema>

export const updateContractCreditLimitsSchema = z
  .object({
    contract: z.object({
      granted_credit: z.string({ message: 'Campo obrigatório' }).min(1, 'Campo obrigatório'),
      default_fuel_price_rule: optionSchema,
      require_product_credit_quota: z.boolean(),
      contract_product_categories_attributes: z.array(contractProductCategorySchema),
      is_consumption_controlled_by_quantity: z.boolean().nullable().optional(),
    }),
  })
  .superRefine((arg, ctx) => {
    const isConsumeControlledByQuantity = arg.contract.is_consumption_controlled_by_quantity === true;

    const hasSomeFixedPriceValue = arg?.contract?.contract_product_categories_attributes?.some((category) => category?.contract_product_category_products?.some((product) => product?.price_rule?.value === 'fixed'))

    if (arg.contract.require_product_credit_quota || hasSomeFixedPriceValue) {
      arg.contract.contract_product_categories_attributes.forEach((category, categoryIndex) => {
        if ((!category.global_value_in_currency || !unFormatCurrency(category.global_value_in_currency)) && !isConsumeControlledByQuantity && arg.contract.require_product_credit_quota) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Campo obrigatório',
            path: ['contract', 'contract_product_categories_attributes', categoryIndex, 'global_value_in_currency'],
          })
        }

        if (
          (!category.global_value_in_unit_for_consumption ||
          !unFormatCurrency(category.global_value_in_unit_for_consumption)) &&
          arg.contract.require_product_credit_quota
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Campo obrigatório',
            path: [
              'contract',
              'contract_product_categories_attributes',
              categoryIndex,
              'global_value_in_unit_for_consumption',
            ],
          })
        }

        if (category.contract_product_category_products?.length) {
          category.contract_product_category_products.forEach((product, productIndex) => {
            if (!product.price_rule?.value) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Campo obrigatório',
                path: [
                  'contract',
                  'contract_product_categories_attributes',
                  categoryIndex,
                  'contract_product_category_products',
                  productIndex,
                  'price_rule',
                ],
              })
            }

            if (product.price_rule?.value === 'fixed') {
              product.contract_product_category_product_prices?.forEach((price, priceIndex) => {
                if (!price.value || !unFormatCurrency(price.value)) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Campo obrigatório',
                    path: [
                      'contract',
                      'contract_product_categories_attributes',
                      categoryIndex,
                      'contract_product_category_products',
                      productIndex,
                      'contract_product_category_product_prices',
                      priceIndex,
                      'value',
                    ],
                  })
                }
              })

              if (product.filterByRegion && !!product.contract_product_category_product_prices?.length) {
                product.contract_product_category_product_prices?.forEach((price, priceIndex) => {
                  if (!price.geo) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      message: 'Campo obrigatório',
                      path: [
                        'contract',
                        'contract_product_categories_attributes',
                        categoryIndex,
                        'contract_product_category_products',
                        productIndex,
                        'contract_product_category_product_prices',
                        priceIndex,
                        'geo',
                      ],
                    })
                  }
                })
              }

              if (product.filterByRegion && !product.contract_product_category_product_prices?.length) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: 'Adicione pelo menos um preço por região',
                  path: [
                    'contract',
                    'contract_product_categories_attributes',
                    categoryIndex,
                    'contract_product_category_products',
                    productIndex,
                    'price_rule',
                  ],
                })
              }
            }
          })

          const productsGlobalValueCurrencySum = category.contract_product_category_products.reduce(
            (sum, product) => sum + unFormatCurrency(product.global_value_in_currency),
            0,
          )
          const productsGlobalValueUnitSum = category.contract_product_category_products.reduce(
            (sum, product) => sum + unFormatUnknownUnit(product.global_value_in_unit_for_consumption),
            0,
          )

          if (productsGlobalValueCurrencySum < unFormatCurrency(category.global_value_in_currency || '') && !isConsumeControlledByQuantity) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O valor da soma da cota total dos produtos é menor que o valor da cota total da categoria',
              path: ['contract', 'contract_product_categories_attributes', categoryIndex, 'global_value_in_currency'],
            })
          }

          if (productsGlobalValueUnitSum < unFormatUnknownUnit(category.global_value_in_unit_for_consumption || '')) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O valor da soma da cota total dos produtos é menor que o valor da cota total da categoria',
              path: [
                'contract',
                'contract_product_categories_attributes',
                categoryIndex,
                'global_value_in_unit_for_consumption',
              ],
            })
          }

          if (productsGlobalValueCurrencySum > unFormatCurrency(category.global_value_in_currency || '') && !isConsumeControlledByQuantity) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O valor da soma da cota total dos produtos é maior que o valor da cota total da categoria',
              path: ['contract', 'contract_product_categories_attributes', categoryIndex, 'global_value_in_currency'],
            })
          }

          if (productsGlobalValueUnitSum > unFormatUnknownUnit(category.global_value_in_unit_for_consumption || '')) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'O valor da soma da cota total dos produtos é maior que o valor da cota total da categoria',
              path: [
                'contract',
                'contract_product_categories_attributes',
                categoryIndex,
                'global_value_in_unit_for_consumption',
              ],
            })
          }
        }
      })
    }
  })

export type UpdateContractCreditLimitsSchema = z.infer<typeof updateContractCreditLimitsSchema>
